import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getRegulationApi } from '@flipster/apis/v1/regulation';
import { RegulationResponse, RegulationStatus } from '@flipster/apis/v1/regulation/types';

import { customVaraFlag, customVaraTemporaryOpenFlag } from '../../flag';
import { authBunja } from '../auth/state';

const spotOnlyRegulationStatus: (RegulationStatus | undefined)[] = ['VARA_REGULATED'];

export const useCheckSpotOnly = () => {
  const { isSpotOnlyRegulationAtom } = useBunja(userRegulationBunja);
  const isSpotOnlyRegulation = useAtomValue(isSpotOnlyRegulationAtom);

  return isSpotOnlyRegulation;
};

export const userRegulationBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const isShowAlertAtom = atom(false);

  const getUserRegulationAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getRegulationApi', isCookieLoggedIn],
      queryFn: getRegulationApi,
      select: (res: AxiosResponse<RegulationResponse>): RegulationResponse => {
        // For debugging purposes, we will change the regulation status to VARA_REGULATED and isVaraTemporaryOpen to true.
        const isVaraTemporaryOpen = customVaraTemporaryOpenFlag() || res.data.isVaraTemporaryOpen;
        const regulationStatus = customVaraFlag() ? 'VARA_REGULATED' : res.data.regulationStatus;
        return {
          ...res.data,
          regulationStatus,
          isVaraTemporaryOpen,
        };
      },
    };
  });

  const isRegulationLoadingAtom = atom((get) => {
    return get(getUserRegulationAtom).isLoading;
  });

  // Alert 노출 후에 UI 를 spot only 에 맞춰서 보여줌
  const isSpotOnlyFromDataAtom = atom((get) => {
    const regulation = get(getUserRegulationAtom);
    const regulationStatus = regulation.data?.regulationStatus;
    return spotOnlyRegulationStatus.includes(regulationStatus);
  });

  // TODO: [sh] This means that VARA is temporarily open. Remove this after VARA is officially open.
  const isVaraTemporaryOpenAtom = atom((get) => {
    const userRegulation = get(getUserRegulationAtom);
    return userRegulation.data?.isVaraTemporaryOpen;
  });

  const userRegulationStatusAtom = atom((get) => {
    const userRegulation = get(getUserRegulationAtom);
    return userRegulation.data?.regulationStatus;
  });

  const isVaraRegulatedAtom = atom((get) => {
    const userRegulationStatus = get(userRegulationStatusAtom);
    return userRegulationStatus === 'VARA_REGULATED';
  });

  const isSpotOnlyRegulationAtom = atom((get) => {
    const userRegulationStatus = get(userRegulationStatusAtom);
    return spotOnlyRegulationStatus.includes(userRegulationStatus);
  });

  const filterSymbolsWithRegulationStatusAtom = atom((get) => {
    const regulation = get(getUserRegulationAtom);
    return regulation.data?.filterSymbolsWithRegulationStatus;
  });

  const shouldFilterToVaraSymbolsAtom = atom((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    const filterSymbols = get(filterSymbolsWithRegulationStatusAtom);
    return isSpotOnly && filterSymbols;
  });

  return {
    isRegulationLoadingAtom,
    isSpotOnlyRegulationAtom,
    isVaraRegulatedAtom,
    isSpotOnlyFromDataAtom,
    isShowAlertAtom,
    isVaraTemporaryOpenAtom,
    getUserRegulationAtom,
    filterSymbolsWithRegulationStatusAtom,
    shouldFilterToVaraSymbolsAtom,
  };
});

userRegulationBunja.debugLabel = 'userRegulationBunja';
